/**
 * Job Tracker API client.
 *
 * Deliberately self-contained rather than added to utils/api.js: the whole
 * module can be deleted as one folder. It mirrors the conventions of
 * utils/api.js exactly — same base URL, same bearer token, same 401 handling,
 * same timeout, same { error } response shape.
 */

const BASE = process.env.REACT_APP_API_URL || '/api';
const REQUEST_TIMEOUT_MS = 30000;

const request = async (method, path, body) => {
  const headers = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('rb_token');
  if (token) headers.Authorization = `Bearer ${token}`;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let res;
  try {
    res = await fetch(`${BASE}/job-tracker${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (e) {
    if (e.name === 'AbortError') throw new Error('Request timed out. Please try again.');
    throw e;
  } finally {
    clearTimeout(timer);
  }

  // Match the app-wide behaviour: a dead token logs the user out everywhere.
  if (res.status === 401) {
    localStorage.removeItem('rb_token');
    window.dispatchEvent(new Event('auth:logout'));
  }

  const contentType = res.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await res.json() : {};
  // Keep the status and payload on the error so callers can tell an upgrade
  // requirement (403) apart from a genuine failure.
  if (!res.ok) throw Object.assign(new Error(data.error || `Request failed (${res.status})`), { ...data, status: res.status });
  return data;
};

export const jobTrackerApi = {
  list:      ()             => request('GET',    ''),
  get:       (id)           => request('GET',    `/${id}`),
  create:    (data)         => request('POST',   '', data),
  update:    (id, data)     => request('PUT',    `/${id}`, data),
  moveStage: (id, stage, order) => request('PATCH', `/${id}/stage`, { stage, order }),
  remove:    (id)           => request('DELETE', `/${id}`),
};
